
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown } from "lucide-react";
import { fuzzySearchCategorized, fuzzyScore } from "@/utils/fuzzySearch";
import { educationCategories, specialEducationOptions } from "@/data/educationLevels";

interface EducationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
  placeholder?: string;
  className?: string;
}

export const EducationAutocomplete: React.FC<EducationAutocompleteProps> = ({
  value,
  onChange,
  error,
  label = "Formação acadêmica",
  placeholder = "Digite ou selecione sua formação",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [internalValue, setInternalValue] = useState(value);
  const justSelectedRef = useRef(false);

  // Sincronizar valor externo com interno quando não estiver buscando
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (value !== internalValue && !searchTerm) {
      setInternalValue(value);
    }
  }, [value, internalValue, searchTerm]);

  // Nova lógica: quando digitando, mostrar TODAS as opções ordenadas por semelhança
  const getAllMatchesSorted = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const allMatches: { option: string; category: string; score: number }[] = [];
    
    Object.entries(educationCategories).forEach(([category, options]) => {
      options.forEach(option => {
        const score = fuzzyScore(searchTerm, option);
        if (score >= 0.3) {  // Lower threshold to show more options
          allMatches.push({ option, category, score });
        }
      });
    });
    
    // Sort by score descending (highest first)
    return allMatches.sort((a, b) => b.score - a.score);
  }, [searchTerm]);
  
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return educationCategories;
    return fuzzySearchCategorized(searchTerm, educationCategories, 0.5);
  }, [searchTerm]);

  const handleSelect = (option: string) => {
    justSelectedRef.current = true;
    setInternalValue(option);
    setSearchTerm("");
    onChange(option);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setInternalValue(newValue);
    setIsOpen(true);
  };

  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 200);
  };

  const handleMouseDownOption = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <div className={`space-y-2 relative ${className}`}>
      <Label className="text-white font-medium">{label}</Label>
      <div className="relative">
        <Input
          value={internalValue || searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          placeholder={!internalValue && !searchTerm ? "Selecione uma opção" : placeholder}
          className={`bg-black/30 border-white/20 text-white pr-10 focus:border-luxury-gold transition-colors ${
            !internalValue && !searchTerm ? 'placeholder:text-red-400' : 'placeholder:text-white/50'
          }`}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 text-white/70"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {isOpen && (
        <Card className="absolute top-full left-0 right-0 z-50 bg-black/90 border-white/20 max-h-80 overflow-y-auto shadow-xl">
          <div className="p-2 space-y-1">
            {/* Nova UX: Ao digitar, mostrar todas opções ordenadas + "Outros" no final */}
            {searchTerm ? (
              <>
                {getAllMatchesSorted.map((match, index) => (
                  <div key={`${match.category}-${match.option}-${index}`}>
                    {index === 0 || getAllMatchesSorted[index - 1].category !== match.category ? (
                      <div className="px-2 py-1 text-xs font-medium text-luxury-gold uppercase tracking-wide">
                        {match.category}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="w-full text-left px-2 py-2 text-sm text-white hover:bg-white/10 rounded flex items-center justify-between"
                      onMouseDown={handleMouseDownOption}
                      onClick={() => handleSelect(match.option)}
                    >
                      <span>{match.option}</span>
                      {value === match.option && <Check className="h-4 w-4 text-luxury-gold" />}
                    </button>
                  </div>
                ))}
                
                {/* Sempre mostrar "Outros" no final */}
                {getAllMatchesSorted.length > 0 && (
                  <div className="border-t border-white/10 mt-2 pt-2">
                    <button
                      type="button"
                      className="w-full text-left px-2 py-2 text-sm text-white hover:bg-white/10 rounded flex items-center justify-between"
                      onMouseDown={handleMouseDownOption}
                      onClick={() => handleSelect("Outros")}
                    >
                      <span className="font-medium">Outros</span>
                      {value === "Outros" && <Check className="h-4 w-4 text-luxury-gold" />}
                    </button>
                  </div>
                )}

                {getAllMatchesSorted.length === 0 && (
                  <>
                    <div className="px-2 py-3 text-sm text-white/70 text-center">
                      Nenhuma correspondência encontrada
                    </div>
                    <div className="border-t border-white/10 mt-2 pt-2">
                      <button
                        type="button"
                        className="w-full text-left px-2 py-2 text-sm text-white hover:bg-white/10 rounded flex items-center justify-between"
                        onMouseDown={handleMouseDownOption}
                        onClick={() => handleSelect("Outros")}
                      >
                        <span className="font-medium">Outros</span>
                        {value === "Outros" && <Check className="h-4 w-4 text-luxury-gold" />}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {/* Lista completa quando não está digitando */}
                {/* Special Options Section */}
                {specialEducationOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="w-full text-left px-2 py-2 text-sm text-white hover:bg-white/10 rounded flex items-center justify-between border-b border-white/10"
                    onMouseDown={handleMouseDownOption}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="font-medium">{option}</span>
                    {value === option && <Check className="h-4 w-4 text-luxury-gold" />}
                  </button>
                ))}
                
                <div className="border-b border-white/20 my-2"></div>

                {/* Regular Categories */}
                {Object.entries(filteredOptions).map(([category, options]) => (
                  <div key={category}>
                    <div className="px-2 py-1 text-xs font-medium text-luxury-gold uppercase tracking-wide">
                      {category}
                    </div>
                    {options?.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className="w-full text-left px-2 py-2 text-sm text-white hover:bg-white/10 rounded flex items-center justify-between"
                        onMouseDown={handleMouseDownOption}
                        onClick={() => handleSelect(option)}
                      >
                        <span>{option}</span>
                        {value === option && <Check className="h-4 w-4 text-luxury-gold" />}
                      </button>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </Card>
      )}

      {error && (
        <p className="text-red-400 text-sm">{error}</p>
      )}
    </div>
  );
};
